import { Injectable, inject } from '@angular/core';
import fourierTransform from 'fourier-transform';
import { IPoint } from 'src/app/user-identification/models/user-identification';
import { ProcessHelperService } from 'src/app/user-identification/services/process-helper.service';
import { IGridAnalysis, IGridCell, IConsecutiveCells } from '../models/image.models';

@Injectable({
	providedIn: 'root',
})
export class ImageHelperService {
	// #region Services
	private processHelperService = inject(ProcessHelperService);
	// #endregion

	private CONSTANT_BRIGHTNESS: number = 0;
	private BRIGHTNESS_THRESHOLD: number = 200;
	private GRID_SIZE: number = 3;
	private BRIGHTNESS_PERCENTAGE: number = 95;
	private BORDER_PERCENTAGE: number = 0.05;
	private cv;

	/** Máximo lado (px) al analizar Laplaciano: menos ruido y coste estable entre resoluciones. */
	private readonly SHARPNESS_LAPLACIAN_MAX_SIDE = 480;
	/** Tamaño fijo potencia de 2 para RFFT 1D por filas/columnas (proxy de altas frecuencias 2D). */
	private readonly SHARPNESS_FOURIER_GRID = 128;
	private readonly SHARPNESS_EPS = 1e-9;

	constructor() {
		this.processHelperService.getOpenCV().subscribe((openCv: any) => {
			this.cv = openCv;
		});
	}

	/** Luminancia BT.601 (misma que el análisis espectral de nitidez). */
	private lumaBt601(r: number, g: number, b: number): number {
		return 0.299 * r + 0.587 * g + 0.114 * b;
	}

	/**
	 * Reduce la imagen si supera SHARPNESS_LAPLACIAN_MAX_SIDE para umbrales más estables y menor coste.
	 */
	private downscaleImageDataIfNeeded(imageData: ImageData): ImageData {
		const w = imageData.width;
		const h = imageData.height;
		const maxSide = this.SHARPNESS_LAPLACIAN_MAX_SIDE;
		if (Math.max(w, h) <= maxSide) return imageData;

		const nw = Math.max(2, Math.round((w * maxSide) / Math.max(w, h)));
		const nh = Math.max(2, Math.round((h * maxSide) / Math.max(w, h)));

		const srcCanvas = document.createElement('canvas');
		srcCanvas.width = w;
		srcCanvas.height = h;
		const sctx = srcCanvas.getContext('2d');
		if (!sctx) return imageData;
		sctx.putImageData(imageData, 0, 0);

		const dst = document.createElement('canvas');
		dst.width = nw;
		dst.height = nh;
		const dctx = dst.getContext('2d');
		if (!dctx) return imageData;
		dctx.drawImage(srcCanvas, 0, 0, w, h, 0, 0, nw, nh);
		return dctx.getImageData(0, 0, nw, nh);
	}

	/** Varianza muestral de la respuesta al kernel Laplaciano 3x3 (BT.601). */
	private laplacianVariance(imageData: ImageData): number {
		const data = imageData.data;
		const width = imageData.width;
		const height = imageData.height;
		if (width < 3 || height < 3) return 0;

		let sum = 0;
		let sumSq = 0;
		let count = 0;

		for (let y = 1; y < height - 1; y++) {
			for (let x = 1; x < width - 1; x++) {
				const L = this.applyKernel(x, y, data, width, height);
				sum += L;
				sumSq += L * L;
				count++;
			}
		}
		if (count === 0) return 0;
		const mean = sum / count;
		return Math.max(0, sumSq / count - mean * mean);
	}

	private laplacianVarianceInRegion(imageData: ImageData, region: IPoint): number {
		const data = imageData.data;
		const width = imageData.width;
		const height = imageData.height;

		const startX = Math.max(1, Math.floor(region.x));
		const endX = Math.min(width - 1, Math.floor(region.x + region.width));
		const startY = Math.max(1, Math.floor(region.y));
		const endY = Math.min(height - 1, Math.floor(region.y + region.height));

		let sum = 0;
		let sumSq = 0;
		let count = 0;

		for (let y = startY; y < endY; y++) {
			for (let x = startX; x < endX; x++) {
				const L = this.applyKernel(x, y, data, width, height);
				sum += L;
				sumSq += L * L;
				count++;
			}
		}
		if (count === 0) return 0;
		const mean = sum / count;
		return Math.max(0, sumSq / count - mean * mean);
	}

	/**
	 * RMS (sqrt de varianza) del Laplaciano: escala comparable a la antigua media de |L| para umbrales.
	 */
	public getLaplacianFilterSharpness(imageData: ImageData): number {
		if (!imageData?.data?.length) return 0;
		return Math.sqrt(this.laplacianVariance(this.downscaleImageDataIfNeeded(imageData)));
	}

	// Filtro Laplaciano solo en la región del documento
	public getLaplacianFilterSharpnessInRegion(imageData: ImageData, region: IPoint): number {
		if (!region) {
			return this.getLaplacianFilterSharpness(imageData);
		}

		const scaled = this.downscaleImageDataIfNeeded(imageData);
		const sx = scaled.width / imageData.width;
		const sy = scaled.height / imageData.height;
		const scaledRegion: IPoint = {
			x: region.x * sx,
			y: region.y * sy,
			width: region.width * sx,
			height: region.height * sy,
		};

		return Math.sqrt(this.laplacianVarianceInRegion(scaled, scaledRegion));
	}

	private applyKernel(x, y, data, width, height) {
		const laplacianKernel = [0, 1, 0, 1, -4, 1, 0, 1, 0];

		let sum = 0;
		for (let ky = -1; ky <= 1; ky++) {
			for (let kx = -1; kx <= 1; kx++) {
				const px = x + kx;
				const py = y + ky;
				if (px >= 0 && px < width && py >= 0 && py < height) {
					const offset = (py * width + px) * 4;
					const r = data[offset];
					const g = data[offset + 1];
					const b = data[offset + 2];
					const grayscale = this.lumaBt601(r, g, b);
					const kernelValue = laplacianKernel[(ky + 1) * 3 + (kx + 1)];
					sum += grayscale * kernelValue;
				}
			}
		}
		return sum;
	}

	public getLaplacianFilterSharpnessWithImage(imageData: ImageData): string {
		const data = imageData.data;
		const width = imageData.width;
		const height = imageData.height;
		let totalLaplacian = 0;
		let count = 0;

		const dataAux: ImageData = new ImageData(imageData.width, imageData.height);
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		canvas.width = imageData.width;
		canvas.height = imageData.height;

		for (let y = 1; y < height - 1; y++) {
			for (let x = 1; x < width - 1; x++) {
				const laplacianValue = this.applyKernel(x, y, data, width, height);
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				totalLaplacian += Math.abs(laplacianValue);
				const outputOffset = (y * width + x) * 4;
				if (Math.abs(laplacianValue) > 4) {
					dataAux.data[outputOffset] = 255; // R
					dataAux.data[outputOffset + 1] = 0; // G
					dataAux.data[outputOffset + 2] = 0; // B
					dataAux.data[outputOffset + 3] = 255; // A
				} else {
					dataAux.data[outputOffset] = 0; // R
					dataAux.data[outputOffset + 1] = 0; // G
					dataAux.data[outputOffset + 2] = 0; // B
					dataAux.data[outputOffset + 3] = 255; // A
				}
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				count++;
			}
		}
		context.putImageData(dataAux, 0, 0);
		return canvas.toDataURL('image/jpeg');
	}

	/** Varianza del Laplaciano 3x3 (BT.601), misma base que getLaplacianFilterSharpness. */
	public getLaplacianVarianceSharpness(imageData: ImageData): number {
		if (!imageData?.data?.length) return 0;
		return this.laplacianVariance(this.downscaleImageDataIfNeeded(imageData));
	}

	/**
	 * Redimensiona a cuadrado NxN (BT.601) para análisis espectral 1D por filas y columnas.
	 */
	private resizeToGraySquare(imageData: ImageData, size: number): Float32Array {
		const srcCanvas = document.createElement('canvas');
		srcCanvas.width = imageData.width;
		srcCanvas.height = imageData.height;
		const sctx = srcCanvas.getContext('2d');
		if (!sctx) return new Float32Array(size * size);
		sctx.putImageData(imageData, 0, 0);

		const dst = document.createElement('canvas');
		dst.width = size;
		dst.height = size;
		const dctx = dst.getContext('2d');
		if (!dctx) return new Float32Array(size * size);
		dctx.drawImage(
			srcCanvas,
			0,
			0,
			imageData.width,
			imageData.height,
			0,
			0,
			size,
			size,
		);

		const img = dctx.getImageData(0, 0, size, size);
		const d = img.data;
		const out = new Float32Array(size * size);
		for (let i = 0, j = 0; i < d.length; i += 4, j++) {
			out[j] = this.lumaBt601(d[i], d[i + 1], d[i + 2]);
		}
		return out;
	}

	/**
	 * Proporción de energía en bins altos del espectro RFFT (excluye DC), por fila o columna.
	 */
	private hfMagnitudeRatioFromSignal(signal: Float32Array): number {
		const spec = fourierTransform(signal);
		const n = spec.length;
		if (n < 2) return 0;

		const kCut = Math.max(1, Math.floor(n / 8));
		let total = this.SHARPNESS_EPS;
		let hf = 0;
		for (let i = 1; i < n; i++) {
			const m = Math.abs(spec[i]);
			total += m;
			if (i >= kCut) hf += m;
		}
		return hf / total;
	}

	/**
	 * Nitidez tipo contenido de altas frecuencias: RFFT 1D en cada fila y columna de una rejilla 128²
	 * (proxy 2D más interpretable que una única FFT sobre el raster completo).
	 * Escala aproximada 0–100 (media de ratios HF).
	 */
	public getFourierSharpness(imageData: ImageData): number {
		if (!imageData?.data?.length || imageData.width < 2 || imageData.height < 2) return 0;

		const N = this.SHARPNESS_FOURIER_GRID;
		const gray = this.resizeToGraySquare(imageData, N);
		const rowBuf = new Float32Array(N);
		let rowAcc = 0;

		for (let y = 0; y < N; y++) {
			for (let x = 0; x < N; x++) rowBuf[x] = gray[y * N + x];
			rowAcc += this.hfMagnitudeRatioFromSignal(rowBuf);
		}

		let colAcc = 0;
		for (let x = 0; x < N; x++) {
			for (let y = 0; y < N; y++) rowBuf[y] = gray[y * N + x];
			colAcc += this.hfMagnitudeRatioFromSignal(rowBuf);
		}

		const avgRatio = (rowAcc + colAcc) / (2 * N);
		return avgRatio * 100;
	}

	// Brillo
	public getBrightness(imageData: ImageData): number {
		if (!imageData) return 0;

		const data = imageData.data;
		let totalBrightness = 0;
		let brightPixels = 0;
		const totalPixels = data.length / 4;

		// Umbral de brillo (puedes ajustar este valor)
		let brightnessThreshold = this.BRIGHTNESS_THRESHOLD;
		if (this.CONSTANT_BRIGHTNESS > 0) {
			for (let i = 0; i < data.length; i += 4) {
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];

				// Calcular el brillo
				const brightness = (r + g + b) / 3;
				totalBrightness += brightness;
			}

			if (totalBrightness / totalPixels < brightnessThreshold) {
				brightnessThreshold = totalBrightness / totalPixels + this.CONSTANT_BRIGHTNESS;
			}
		}

		for (let i = 0; i < data.length; i += 4) {
			const r = data[i];
			const g = data[i + 1];
			const b = data[i + 2];

			// Calcular el brillo
			const brightness = (r + g + b) / 3;
			if (brightness > brightnessThreshold) {
				brightPixels++;
			}
		}

		return (brightPixels / totalPixels) * 100;
	}

	public getImageBrightness(imageData: ImageData): string {
		const data = imageData.data;
		let totalBrightness = 0;
		const dataAux: ImageData = new ImageData(imageData.width, imageData.height);
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		canvas.width = imageData.width;
		canvas.height = imageData.height;
		let brightPixels = 0;
		const totalPixels = data.length / 4;

		// Umbral de brillo (puedes ajustar este valor)
		let brightnessThreshold = this.BRIGHTNESS_THRESHOLD;
		if (this.CONSTANT_BRIGHTNESS > 0) {
			for (let i = 0; i < data.length; i += 4) {
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];

				// Calcular el brillo
				const brightness = (r + g + b) / 3;
				totalBrightness += brightness;
			}

			if (totalBrightness / totalPixels < brightnessThreshold) {
				brightnessThreshold = totalBrightness / totalPixels + this.CONSTANT_BRIGHTNESS;
			}
		}

		for (let i = 0; i < data.length; i += 4) {
			const r = data[i];
			const g = data[i + 1];
			const b = data[i + 2];

			// Calcular el brillo
			const brightness = (r + g + b) / 3;
			if (brightness > brightnessThreshold) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				brightPixels++;
				dataAux.data[i] = 255; // R
				dataAux.data[i + 1] = 0; // G
				dataAux.data[i + 2] = 0; // B
				dataAux.data[i + 3] = 255; // A
			} else {
				dataAux.data[i] = 0; // R
				dataAux.data[i + 1] = 0; // G
				dataAux.data[i + 2] = 0; // B
				dataAux.data[i + 3] = 255; // A
			}
		}

		context.putImageData(dataAux, 0, 0);

		return canvas.toDataURL('image/jpeg');
	}

	public getImageGridBrightness(
		imageData: ImageData,
		gridSize: number = this.GRID_SIZE,
		brightnessThreshold: number = this.BRIGHTNESS_THRESHOLD,
	): string {
		const data = imageData.data;
		const width = imageData.width;
		const height = imageData.height;

		// Calcular bordes (10% descartado, 5% en cada lado)
		const cropPercentage = this.BORDER_PERCENTAGE;
		const cropLeft = Math.floor(width * cropPercentage);
		const cropTop = Math.floor(height * cropPercentage);
		const cropRight = Math.floor(width * (1 - cropPercentage));
		const cropBottom = Math.floor(height * (1 - cropPercentage));
		const croppedWidth = cropRight - cropLeft;
		const croppedHeight = cropBottom - cropTop;

		// Crear canvas y contexto
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		canvas.width = width;
		canvas.height = height;

		// Crear ImageData para los píxeles coloreados
		const dataAux: ImageData = new ImageData(width, height);

		// Colorear píxeles: negro si < threshold, rojo si >= threshold
		// Solo procesar el 90% central (descartando 5% en cada borde)
		for (let y = 0; y < height; y++) {
			for (let x = 0; x < width; x++) {
				const pixelIndex = (y * width + x) * 4;
				const r = data[pixelIndex];
				const g = data[pixelIndex + 1];
				const b = data[pixelIndex + 2];

				// Verificar si está dentro del área de recorte
				if (x >= cropLeft && x < cropRight && y >= cropTop && y < cropBottom) {
					// Calcular brillo promedio del píxel
					const brightness = (r + g + b) / 3;

					if (brightness >= brightnessThreshold) {
						dataAux.data[pixelIndex] = 255; // R - Rojo
						dataAux.data[pixelIndex + 1] = 0; // G
						dataAux.data[pixelIndex + 2] = 0; // B
						dataAux.data[pixelIndex + 3] = 255; // A
					} else {
						dataAux.data[pixelIndex] = 0; // R - Negro
						dataAux.data[pixelIndex + 1] = 0; // G
						dataAux.data[pixelIndex + 2] = 0; // B
						dataAux.data[pixelIndex + 3] = 255; // A
					}
				} else {
					// Píxeles fuera del área son negros
					dataAux.data[pixelIndex] = 0; // R
					dataAux.data[pixelIndex + 1] = 0; // G
					dataAux.data[pixelIndex + 2] = 0; // B
					dataAux.data[pixelIndex + 3] = 255; // A
				}
			}
		}

		// Poner la imagen coloreada en el canvas
		context.putImageData(dataAux, 0, 0);

		// Dibujar la cuadrícula y calcular porcentajes
		const cellWidth = croppedWidth / gridSize;
		const cellHeight = croppedHeight / gridSize;

		context.strokeStyle = '#00FF00'; // Verde para la cuadrícula
		context.lineWidth = 2;

		// Dibujar líneas verticales
		for (let col = 1; col < gridSize; col++) {
			const x = cropLeft + col * cellWidth;
			context.beginPath();
			context.moveTo(x, cropTop);
			context.lineTo(x, cropBottom);
			context.stroke();
		}

		// Dibujar líneas horizontales
		for (let row = 1; row < gridSize; row++) {
			const y = cropTop + row * cellHeight;
			context.beginPath();
			context.moveTo(cropLeft, y);
			context.lineTo(cropRight, y);
			context.stroke();
		}

		// Dibujar porcentajes en el centro de cada celda
		context.fillStyle = '#FFFFFF'; // Texto blanco
		context.font = `bold ${Math.floor(cellHeight / 4)}px Arial`;
		context.textAlign = 'center';
		context.textBaseline = 'middle';

		for (let row = 0; row < gridSize; row++) {
			for (let col = 0; col < gridSize; col++) {
				const startX = Math.floor(cropLeft + col * cellWidth);
				const endX = Math.floor(cropLeft + (col + 1) * cellWidth);
				const startY = Math.floor(cropTop + row * cellHeight);
				const endY = Math.floor(cropTop + (row + 1) * cellHeight);

				// Contar píxeles rojos en esta celda
				let redPixels = 0;
				let totalPixels = 0;

				for (let y = startY; y < endY; y++) {
					for (let x = startX; x < endX; x++) {
						const pixelIndex = (y * width + x) * 4;
						const r = dataAux.data[pixelIndex];
						const g = dataAux.data[pixelIndex + 1];
						const b = dataAux.data[pixelIndex + 2];

						// Un píxel es rojo si R=255, G=0, B=0
						if (r === 255 && g === 0 && b === 0) {
							redPixels++;
						}
						totalPixels++;
					}
				}

				const percentage = totalPixels > 0 ? Math.round((redPixels / totalPixels) * 100) : 0;
				const centerX = startX + cellWidth / 2;
				const centerY = startY + cellHeight / 2;

				// Dibujar fondo semitransparente detrás del texto
				context.fillStyle = 'rgba(0, 0, 0, 0.7)';
				const textWidth = 50;
				const textHeight = 30;
				context.fillRect(centerX - textWidth / 2, centerY - textHeight / 2, textWidth, textHeight);

				// Dibujar el porcentaje
				context.fillStyle = '#FFFFFF';
				context.fillText(`${percentage}%`, centerX, centerY);
			}
		}

		return canvas.toDataURL('image/jpeg');
	}

	// Dividir imagen en cuadrícula y calcular porcentaje de píxeles por encima del umbral
	public analyzeImageGrid(
		imageData: ImageData,
		gridSize: number = this.GRID_SIZE,
		brightnessThreshold: number = this.BRIGHTNESS_THRESHOLD,
	): IGridAnalysis {
		if (gridSize < 2 || gridSize > 4) {
			throw new Error('Grid size must be between 2 and 4');
		}

		const data = imageData.data;
		const width = imageData.width;
		const height = imageData.height;

		// Calcular bordes (10% descartado, 5% en cada lado)
		const cropPercentage = this.BORDER_PERCENTAGE;
		const cropLeft = Math.floor(width * cropPercentage);
		const cropTop = Math.floor(height * cropPercentage);
		const cropRight = Math.floor(width * (1 - cropPercentage));
		const cropBottom = Math.floor(height * (1 - cropPercentage));
		const croppedWidth = cropRight - cropLeft;
		const croppedHeight = cropBottom - cropTop;

		// Calcular tamaño de cada celda
		const cellWidth = croppedWidth / gridSize;
		const cellHeight = croppedHeight / gridSize;

		const cells: IGridCell[] = [];

		// Iterar sobre cada celda de la cuadrícula
		for (let row = 0; row < gridSize; row++) {
			for (let col = 0; col < gridSize; col++) {
				const startX = Math.floor(cropLeft + col * cellWidth);
				const endX = Math.floor(cropLeft + (col + 1) * cellWidth);
				const startY = Math.floor(cropTop + row * cellHeight);
				const endY = Math.floor(cropTop + (row + 1) * cellHeight);

				let brightPixels = 0;
				let totalPixels = 0;

				// Contar píxeles brillantes en esta celda
				for (let y = startY; y < endY; y++) {
					for (let x = startX; x < endX; x++) {
						const pixelIndex = (y * width + x) * 4;
						const r = data[pixelIndex];
						const g = data[pixelIndex + 1];
						const b = data[pixelIndex + 2];

						// Calcular brillo promedio del píxel
						const brightness = (r + g + b) / 3;

						if (brightness > brightnessThreshold) {
							brightPixels++;
						}
						totalPixels++;
					}
				}

				// Calcular porcentaje
				const brightnessPercentage = totalPixels > 0 ? (brightPixels / totalPixels) * 100 : 0;

				cells.push({
					row,
					col,
					brightnessPercentage,
				});
			}
		}

		return {
			gridSize,
			threshold: brightnessThreshold,
			cells,
		};
	}

	// Encontrar celdas consecutivas (adyacentes) con porcentaje mayor al umbral especificado
	public findConsecutiveBrightCells(
		gridAnalysis: IGridAnalysis,
		percentageThreshold: number = this.BRIGHTNESS_PERCENTAGE,
	): IConsecutiveCells[] {
		if (!gridAnalysis) return [];
		const gridSize = gridAnalysis.gridSize;
		const cells = gridAnalysis.cells;
		const visited = new Set<string>();
		const consecutiveGroups: IConsecutiveCells[] = [];

		// Función para obtener celdas adyacentes (incluidas diagonales)
		const getAdjacentCells = (row: number, col: number): Array<{ row: number; col: number }> => {
			const adjacent: Array<{ row: number; col: number }> = [];

			for (let r = row - 1; r <= row + 1; r++) {
				for (let c = col - 1; c <= col + 1; c++) {
					// Omitir la celda misma
					if ((r !== row || c !== col) && r >= 0 && r < gridSize && c >= 0 && c < gridSize) {
						adjacent.push({ row: r, col: c });
					}
				}
			}

			return adjacent;
		};

		// Función para hacer BFS y encontrar celdas conectadas
		const bfs = (startRow: number, startCol: number): IGridCell[] => {
			const group: IGridCell[] = [];
			const queue: Array<{ row: number; col: number }> = [{ row: startRow, col: startCol }];
			const key = `${startRow},${startCol}`;

			if (visited.has(key)) {
				return group;
			}

			visited.add(key);

			while (queue.length > 0) {
				const { row, col } = queue.shift()!;

				// Encontrar la celda en el array
				const cell = cells.find((c) => c.row === row && c.col === col);
				if (cell && cell.brightnessPercentage >= percentageThreshold) {
					group.push(cell);

					// Explorar celdas adyacentes
					const adjacent = getAdjacentCells(row, col);
					for (const adj of adjacent) {
						const adjKey = `${adj.row},${adj.col}`;
						if (!visited.has(adjKey)) {
							visited.add(adjKey);
							queue.push(adj);
						}
					}
				}
			}

			return group;
		};

		// Buscar todos los grupos de celdas consecutivas
		for (const cell of cells) {
			if (cell.brightnessPercentage >= percentageThreshold) {
				const key = `${cell.row},${cell.col}`;
				if (!visited.has(key)) {
					const group = bfs(cell.row, cell.col);
					// Solo agregar grupos con 2 o más celdas consecutivas
					if (group.length >= 2) {
						consecutiveGroups.push({
							cells: group,
							percentage: percentageThreshold,
						});
					}
				}
			}
		}

		return consecutiveGroups;
	}

	// Obtener rectangulo dentro de una foto
	public getDocumentRect(canvas: HTMLCanvasElement): IPoint {
		let documentRect: IPoint = { x: 0, y: 0, width: 0, height: 0 };
		// Convertir la imagen del canvas en un Mat de OpenCV
		const src = this.cv.imread(canvas);
		const dst = new this.cv.Mat();

		// Convertir a escala de grises
		this.cv.cvtColor(src, dst, this.cv.COLOR_RGBA2GRAY, 0);

		// Aplicar filtro Canny (valores umbral ajustables)
		this.cv.Canny(dst, dst, 50, 150);

		// Aplicar la Transformada de Hough para detectar líneas
		const lines = new this.cv.Mat();
		this.cv.HoughLinesP(dst, lines, 1, Math.PI / 180, 100, 75, 20);

		// Dividimos las líneas entre horizontales y verticales
		const tolerance = 5;
		const horizontalLines: Array<any> = [];
		const verticalLines: Array<any> = [];
		for (let i = 0; i < lines.rows; ++i) {
			const x1 = lines.data32S[i * 4];
			const y1 = lines.data32S[i * 4 + 1];
			const x2 = lines.data32S[i * 4 + 2];
			const y2 = lines.data32S[i * 4 + 3];
			if (Math.abs(y1 - y2) < tolerance) {
				horizontalLines.push({ x1: x1, y1: y1, x2: x2, y2: y2 });
			} else if (Math.abs(x1 - x2) < tolerance) {
				verticalLines.push({ x1: x1, y1: y1, x2: x2, y2: y2 });
			}
		}
		const minHx1 = Math.min(...horizontalLines.map((item) => item.x1));
		const minHx2 = Math.min(...horizontalLines.map((item) => item.x2));
		const minVx1 = Math.min(...verticalLines.map((item) => item.x1));
		const minVx2 = Math.min(...verticalLines.map((item) => item.x2));
		const minHy1 = Math.min(...horizontalLines.map((item) => item.y1));
		const minHy2 = Math.min(...horizontalLines.map((item) => item.y2));
		const minVy1 = Math.min(...verticalLines.map((item) => item.y1));
		const minVy2 = Math.min(...verticalLines.map((item) => item.y2));
		const maxHx1 = Math.max(...horizontalLines.map((item) => item.x1));
		const maxHx2 = Math.max(...horizontalLines.map((item) => item.x2));
		const maxVx1 = Math.max(...verticalLines.map((item) => item.x1));
		const maxVx2 = Math.max(...verticalLines.map((item) => item.x2));
		const maxHy1 = Math.max(...horizontalLines.map((item) => item.y1));
		const maxHy2 = Math.max(...horizontalLines.map((item) => item.y2));
		const maxVy1 = Math.max(...verticalLines.map((item) => item.y1));
		const maxVy2 = Math.max(...verticalLines.map((item) => item.y2));

		if (horizontalLines.length || verticalLines.length) {
			documentRect = {
				x: Math.min(minHx1, minHx2, minVx1, minVx2),
				y: Math.min(minHy1, minHy2, minVy1, minVy2),
				width: Math.max(maxHx1, maxHx2, maxVx1, maxVx2) - Math.min(minHx1, minHx2, minVx1, minVx2),
				height: Math.max(maxHy1, maxHy2, maxVy1, maxVy2) - Math.min(minHy1, minHy2, minVy1, minVy2),
			};
		} else {
			console.log('No podemos crear un rectangulo');
		}

		// Liberar memoria de OpenCV
		src.delete();
		dst.delete();
		lines.delete();
		return documentRect;
	}
}
